import React from 'react';
import { Card, CardBody, Badge } from 'reactstrap';
import { Bot, Zap, Brain, FileText, Search, Globe, Code, Sparkles } from 'lucide-react';

const AgentCard = ({ agent, onSelect, compact = false, themeColor = '#6366f1' }) => {
  // Get appropriate icon based on agent type or name
  const getAgentIcon = () => {
    const name = agent.name?.toLowerCase() || '';
    const desc = agent.description?.toLowerCase() || '';
    
    if (name.includes('search') || desc.includes('search')) return Search;
    if (name.includes('doc') || desc.includes('document')) return FileText;
    if (name.includes('code') || desc.includes('develop')) return Code;
    if (name.includes('web') || desc.includes('internet')) return Globe;
    if (name.includes('smart') || desc.includes('intelligent')) return Brain;
    if (agent.type === 'assistant') return Sparkles;
    
    return Bot;
  };

  const Icon = getAgentIcon();

  if (compact) {
    return (
      <Card 
        className="agent-card compact"
        onClick={() => onSelect(agent)}
        style={{ 
          cursor: 'pointer',
          marginBottom: '8px',
          border: '1px solid #e2e8f0',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: themeColor,
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }
        }}
      >
        <CardBody style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div 
              style={{ 
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: `linear-gradient(135deg, ${themeColor}20, ${themeColor}40)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Icon size={20} style={{ color: themeColor }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h6 style={{ 
                margin: 0, 
                fontSize: '14px', 
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {agent.name}
              </h6>
              <p style={{ 
                margin: 0, 
                fontSize: '12px', 
                color: '#64748b',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginTop: '2px'
              }}>
                {agent.description}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card 
      className="agent-card"
      onClick={() => onSelect(agent)}
      style={{ 
        cursor: 'pointer',
        height: '100%',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        transition: 'all 0.2s ease',
        overflow: 'hidden'
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = themeColor;
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = '#e2e8f0';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <CardBody style={{ padding: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div 
              style={{ 
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${themeColor}20, ${themeColor}40)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}
            >
              <Icon size={24} style={{ color: themeColor }} />
            </div>
            <div style={{ flex: 1 }}>
              <h5 style={{ 
                margin: 0, 
                fontSize: '16px', 
                fontWeight: 600,
                marginBottom: '4px'
              }}>
                {agent.name}
              </h5>
              {agent.type && (
                <Badge 
                  color="secondary" 
                  style={{ 
                    fontSize: '10px',
                    padding: '2px 6px',
                    backgroundColor: themeColor + '20',
                    color: themeColor
                  }}
                >
                  {agent.type === 'assistant' ? 'Assistant' : 'Agent'}
                </Badge>
              )}
            </div>
          </div>
          
          <p style={{ 
            fontSize: '13px', 
            color: '#64748b', 
            marginBottom: '12px',
            flex: 1,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}>
            {agent.description || 'AI-powered assistant'}
          </p>
          
          {agent.capabilities && agent.capabilities.length > 0 && (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {agent.capabilities.slice(0, 3).map((cap, idx) => (
                <Badge 
                  key={idx}
                  style={{ 
                    fontSize: '11px',
                    fontWeight: 'normal',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    padding: '2px 8px'
                  }}
                >
                  <Zap size={10} style={{ marginRight: '2px' }} />
                  {cap}
                </Badge>
              ))}
              {agent.capabilities.length > 3 && (
                <Badge 
                  style={{ 
                    fontSize: '11px',
                    fontWeight: 'normal',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    padding: '2px 8px'
                  }}
                >
                  +{agent.capabilities.length - 3}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default AgentCard;